/**
 * Maps a GetExecutionDetail `ExecutionDetail` to the `ExecutionDetailView` view
 * model.
 *
 * executionDetails is a polymorphic document (shape per executionType). As on
 * the artifact page it is decomposed by the known DAO keys, surfacing only the
 * parts that are present:
 *   - scalar fields                -> detailFields
 *   - latestSummary                -> bankSummary
 *   - latestProcessorResponse      -> processorResponse
 *   - responses[] (ResponseEntry)  -> responses (rawResponse as a JSON view)
 *   - response (free-form)         -> oslResponse (pretty JSON)
 * The direction/system waterfall (directions) is mapped in when that section is built.
 */
import { ExecutionDetail } from './executionDetailApi';
import { ExecutionDetailView } from './executionDetailTypes';
import { KeyValue } from '../paymentDetail/paymentDetailTypes';
import {
  bottleneckLabel, formatMs, fullTimestamp, titleCase, toDirection, toFlowState,
} from '../paymentDetail/paymentDetailFormat';
import { toFlow } from '../paymentDetail/flowGraphMapper';
import { dash, prettyJson, toDetailFields, kvPresent, toResponses } from '../paymentDetail/detailsDocument';

/** Top-level ExecutionDetail scalar fields, in display order; "-" when empty. */
const toIdentifiers = (detail: ExecutionDetail): KeyValue[] => [
  { key: 'executionType', value: dash(detail.executionType) },
  { key: 'workflowArn', value: dash(detail.workflowArn) },
  { key: 'parentExecutionId', value: dash(detail.parentExecutionId) },
  { key: 'artifactId', value: dash(detail.artifactId) },
  { key: 'createdAt', value: detail.createdAt ? fullTimestamp(detail.createdAt) : '-' },
  { key: 'updatedAt', value: detail.updatedAt ? fullTimestamp(detail.updatedAt) : '-' },
];

/* ------------------------------------------------------------------ */
/* Entry point                                                         */
/* ------------------------------------------------------------------ */

export const mapExecutionDetail = (detail: ExecutionDetail): ExecutionDetailView => {
  const ed = detail.executionDetails ?? {};
  const overallState = toFlowState(detail.beaconExecutionState);
  const currentDirection = toDirection(detail.currentDirection);
  const latestSummary = ed.latestSummary as Record<string, unknown> | undefined;
  const latestProcessorResponse = ed.latestProcessorResponse as Record<string, unknown> | undefined;

  return {
    id: detail.id,
    statusLabel: `${detail.beaconExecutionStatus} · ${detail.beaconExecutionState}`,
    statusType: overallState === 'SUCCESS' ? 'success' : overallState === 'FAILED' ? 'error' : 'in-progress',
    locationBadge: `Currently at ${detail.currentSystem} · ${titleCase(currentDirection)}`,
    identifiers: toIdentifiers(detail),
    detailFields: toDetailFields(ed),
    bankSummary: kvPresent(latestSummary, ['status', 'detailedStatusCode', 'detailedStatusCodeCategory']),
    processorResponse: kvPresent(latestProcessorResponse, ['statusCode', 'detailedStatusCode', 'detailedMessage']),
    responses: toResponses(ed),
    oslResponse: prettyJson(ed.response),
    correlationTags: detail.correlationTags ?? [],
    // No responseDetailsById on this operation, so inbound gaps stay unset —
    // an execution has no bank response receipts to measure them against.
    flow: toFlow(detail.graphs ?? [], detail.totalLatencyMs),
    bottleneckLabel: bottleneckLabel(detail.bottleneck),
    totalLatency: formatMs(detail.totalLatencyMs),
  };
};
